import apiEndpoint from '../components/config/data';
import { getAuthHeader } from './tokenService';

/**
 * Helper function for making authenticated API calls
 * Automatically uses the correct authorization header based on token type
 * @param {string} url - The API endpoint URL (relative to apiEndpoint)
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise} - Promise that resolves to the response
 */
export const fetchWithAuth = async (url, options = {}) => {
  const authHeader = getAuthHeader();

  // Merge headers properly to avoid overwriting
  const mergedHeaders = {
    ...(authHeader ? { Authorization: authHeader } : {}),
    ...options.headers,
  };

  const defaultOptions = {
    method: 'GET',
    credentials: 'include',
    ...options,
    headers: mergedHeaders,
  };

  const fullUrl = url.startsWith('http') ? url : `${apiEndpoint}${url}`;

  const response = await fetch(fullUrl, defaultOptions);

  // Handle authentication errors
  if (response.status === 401) {
    // Clear tokens and redirect to login
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('currentUserId');
    localStorage.removeItem('auth_type');

    // Check for SSO cookies and redirect appropriately
    const hasPA_AUTH = document.cookie.includes('PA_AUTH');
    const hasPABE_JWT = document.cookie.includes('PABE_JWT');

    if (hasPA_AUTH || hasPABE_JWT) {
      // Clear invalid SSO cookies
      if (hasPA_AUTH) {
        document.cookie =
          'PA_AUTH=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.spoton.com;';
      }
      if (hasPABE_JWT) {
        document.cookie =
          'PABE_JWT=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.spoton.com;';
      }

      // Redirect to BFF for re-authentication
      const bffUrl = process.env.REACT_APP_PROFIT_ASSIST_BFF_URL;
      if (bffUrl) {
        const returnUrl = encodeURIComponent(
          window.location.pathname + window.location.search
        );
        window.location.href = `${bffUrl}/api/v1/auth/bootstrap?redirect=${returnUrl}`;
      } else {
        window.location.href = '/login';
      }
    } else {
      window.location.href = '/login';
    }

    throw new Error('Authentication expired');
  }

  return response;
};

/**
 * Helper function for making authenticated API calls that expect JSON responses
 * @param {string} url - The API endpoint URL (relative to apiEndpoint)
 * @param {Object} options - Fetch options (method, body, etc.)
 * @returns {Promise} - Promise that resolves to the JSON data
 */
export const fetchJsonWithAuth = async (url, options = {}) => {
  const response = await fetchWithAuth(url, options);

  if (!response.ok) {
    throw new Error(`API call failed with status: ${response.status}`);
  }

  return response.json();
};
